import React, { useState, useEffect } from "react";
import { Play, Pause, SkipForward, SkipBack, RefreshCcw } from "lucide-react";

const styles = {
  container: {
    maxWidth: "500px",
    margin: "0 auto",
    padding: "24px",
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    fontFamily: "Arial, sans-serif",
  },
  title: {
    fontSize: "24px",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: "24px",
  },
  autoplayContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    marginBottom: "16px",
  },
  switch: {
    position: "relative",
    display: "inline-block",
    width: "44px",
    height: "24px",
  },
  switchInput: {
    opacity: 0,
    width: 0,
    height: 0,
  },
  switchSlider: {
    position: "absolute",
    cursor: "pointer",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#ccc",
    borderRadius: "24px",
    transition: "0.4s",
  },
  switchSliderBefore: {
    position: "absolute",
    content: '""',
    height: "20px",
    width: "20px",
    left: "2px",
    bottom: "2px",
    backgroundColor: "white",
    borderRadius: "50%",
    transition: "0.4s",
  },
  wordContainer: {
    textAlign: "center",
    marginBottom: "24px",
  },
  wordCount: {
    fontSize: "14px",
    color: "#666",
    marginBottom: "8px",
  },
  english: {
    fontSize: "28px",
    fontWeight: "bold",
    marginBottom: "8px",
  },
  chinese: {
    fontSize: "18px",
    marginBottom: "8px",
  },
  repetitionCount: {
    fontSize: "14px",
    color: "#666",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "16px",
    marginBottom: "16px",
  },
  button: {
    padding: "8px 16px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "16px",
    minWidth: "90px",
  },
  primaryButton: {
    backgroundColor: "#2196F3",
    color: "white",
  },
  secondaryButton: {
    backgroundColor: "#f0f0f0",
    color: "#333",
  },
  resetButton: {
    backgroundColor: "white",
    border: "1px solid #ccc",
    color: "#333",
  },
  inputContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    justifyContent: "center",
    marginBottom: "16px",
  },
  input: {
    padding: "8px",
    width: "60px",
    fontSize: "14px",
    borderRadius: "4px",
    border: "1px solid #ccc",
    textAlign: "center",
  },
  goButton: {
    padding: "8px 16px",
    backgroundColor: "#4CAF50",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
  },
};

const VocabPracticeApp = () => {
  const [vocabularyData, setVocabularyData] = useState([]);
  const [currentWord, setCurrentWord] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [repetitionCount, setRepetitionCount] = useState(0);
  const [autoplay, setAutoplay] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState("");

  useEffect(() => {
    const loadVocabularyData = async () => {
      try {
        const response = await fetch("/api/vocabulary.json");
        const data = await response.json();
        setVocabularyData(data.vocabulary);
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading vocabulary:", error);
        const data = {
          vocabulary: [
            { english: "abandon", partOfSpeech: "v.", chinese: "放弃" },
            { english: "ability", partOfSpeech: "n.", chinese: "能力； 才能" },
            { english: "able", partOfSpeech: "a.", chinese: "能够；有能力的" },
          ],
        };
        setVocabularyData(data.vocabulary);
        setIsLoading(false);
      }
    };

    loadVocabularyData();
  }, []);

  const moveToNextWord = () => {
    setCurrentWord((prev) => {
      const nextWord = (prev + 1) % vocabularyData.length;
      return nextWord;
    });
    setRepetitionCount(0);
  };

  const speak = (text, isEnglish = true) => {
    if (!isPlaying || !vocabularyData.length) return;

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = isEnglish ? "en-US" : "zh-CN";
    utterance.rate = 0.8;

    utterance.onend = () => {
      if (!isPlaying) return;
      if (isEnglish) {
        setTimeout(() => {
          if (isPlaying) speak(vocabularyData[currentWord].chinese, false);
        }, 500);
        setRepetitionCount((prev) => {
          console.log("en end, " + prev);
          if (prev < 3) {
            return prev + 1;
          } else {
            return prev;
          }
        });
      } else {
        setRepetitionCount((prev) => {
          console.log("cn end, " + prev);
          if (prev < 3) {
            setTimeout(() => {
              if (isPlaying) speak(vocabularyData[currentWord].english, true);
            }, 1000);
          } else if (autoplay) {
            window.speechSynthesis.cancel();
            setTimeout(() => {
              if (isPlaying) moveToNextWord();
            }, 1000);
          } else {
            stopPractice();
          }
          return prev;
        });
      }
    };

    window.speechSynthesis.speak(utterance);
  };

  useEffect(() => {
    if (repetitionCount > 2) setRepetitionCount(0);
    if (isPlaying && vocabularyData.length) {
      console.log("play");
      speak(vocabularyData[currentWord].english, true);
    }
  }, [isPlaying]);

  useEffect(() => {
    if (isPlaying && autoplay && vocabularyData.length) {
      console.log("switch word");
      speak(vocabularyData[currentWord].english, true);
    }
  }, [currentWord]);

  const startPractice = () => {
    setIsPlaying(true);
  };

  const stopPractice = () => {
    window.speechSynthesis.cancel();
    setIsPlaying(false);
  };

  const previousWord = () => {
    window.speechSynthesis.cancel();
    setIsPlaying(false);
    setRepetitionCount(0);
    setCurrentWord(
      (prev) => (prev - 1 + vocabularyData.length) % vocabularyData.length
    );
  };

  const nextWord = () => {
    window.speechSynthesis.cancel();
    setIsPlaying(false);
    setRepetitionCount(0);
    moveToNextWord();
  };

  const resetPractice = () => {
    window.speechSynthesis.cancel();
    setIsPlaying(false);
    setRepetitionCount(0);
    setCurrentWord(0);
  };

  const handleIndexSubmit = (e) => {
    e.preventDefault();
    const index = parseInt(selectedIndex);
    if (!isNaN(index) && index >= 1 && index <= vocabularyData.length) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
      setRepetitionCount(0);
      setCurrentWord(index - 1);
    }
  };

  const handleIndexChange = (e) => {
    const value = e.target.value;
    // Only allow numbers and empty string
    if (value === "" || /^\d+$/.test(value)) {
      setSelectedIndex(value);
    }
  };

  useEffect(() => {
    if (autoplay && !isPlaying && vocabularyData.length) {
      startPractice();
    }
  }, [autoplay, vocabularyData]);

  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
    };
  }, []);

  if (isLoading) {
    return (
      <div style={styles.container}>
        <h1 style={styles.title}>加载词汇表中...</h1>
      </div>
    );
  }

  if (!vocabularyData.length) {
    return (
      <div style={styles.container}>
        <h1 style={styles.title}>加载词汇表失败</h1>
      </div>
    );
  }

  const currentVocab = vocabularyData[currentWord];

  const getSwitchStyles = () => ({
    ...styles.switchSlider,
    backgroundColor: autoplay ? "#2196F3" : "#ccc",
    "&:before": {
      ...styles.switchSliderBefore,
      transform: autoplay ? "translateX(20px)" : "translateX(0)",
    },
  });

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>高考英语词汇练习</h1>

      <div style={styles.inputContainer}>
        <form onSubmit={handleIndexSubmit}>
          <input
            type="text"
            value={selectedIndex}
            onChange={handleIndexChange}
            placeholder={`1-${vocabularyData.length}`}
            style={styles.input}
          />
          <button type="submit" style={styles.goButton}>
            跳转
          </button>
        </form>
      </div>

      <div style={styles.autoplayContainer}>
        <span style={{ fontSize: "14px" }}>自动播放</span>
        <label style={styles.switch}>
          <input
            type="checkbox"
            checked={autoplay}
            onChange={(e) => setAutoplay(e.target.checked)}
            style={styles.switchInput}
          />
          <span style={getSwitchStyles()} />
        </label>
      </div>

      <div style={styles.wordContainer}>
        <div style={styles.wordCount}>
          词汇 {currentWord + 1}/{vocabularyData.length}
        </div>
        <div style={styles.english}>{currentVocab.english}</div>
        <div style={styles.chinese}>
          {currentVocab.partOfSpeech} {currentVocab.chinese}
        </div>
        <div style={styles.repetitionCount}>重复次数: {repetitionCount}/3</div>
      </div>

      <div style={styles.buttonContainer}>
        <button
          onClick={previousWord}
          style={{ ...styles.button, ...styles.secondaryButton }}
        >
          <SkipBack />
        </button>

        <button
          onClick={isPlaying ? stopPractice : startPractice}
          style={{ ...styles.button, ...styles.primaryButton }}
        >
          {isPlaying ? <Pause /> : <Play />}
        </button>

        <button
          onClick={nextWord}
          style={{ ...styles.button, ...styles.secondaryButton }}
        >
          <SkipForward />
        </button>
      </div>

      <div style={styles.buttonContainer}>
        <button
          onClick={resetPractice}
          style={{ ...styles.button, ...styles.resetButton }}
        >
          <RefreshCcw />
        </button>
      </div>
    </div>
  );
};

export default VocabPracticeApp;
